import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import ProjectCard from '../../components/ProjectCard';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const projectsDir = path.join(process.cwd(), 'pages/projects');
  const folders = fs.readdirSync(projectsDir).filter((name) => {
    const fullPath = path.join(projectsDir, name);
    return fs.statSync(fullPath).isDirectory() && name !== 'index.js';
  });

  const projects = await Promise.all(
    folders.map(async (folder) => {
      const { metadata } = await import(`./${folder}/index.js`);
      return {
        ...metadata,
        link: `/projects/${folder}`,
      };
    })
  );

  return { props: { projects } };
}

export default function ProjectsPage({ projects }) {
  // Group 3,2,3,2 pattern
  const grouped = [];
  let i = 0;
  let useThree = true;
  while (i < projects.length) {
    const count = useThree ? 3 : 2;
    grouped.push(projects.slice(i, i + count));
    i += count;
    useThree = !useThree;
  }

  return (
    <>
      <Head>
        <title>Featured Home Remodels Projects | Modern Build</title>
        <meta name="title" content="Featured Home Remodels Projects | Modern Build" />
        <meta name="description" content="Discover Modern Build’s portfolio of luxury remodeling projects in Los Angeles — from custom kitchens and bathrooms to full home renovations and ADU transformations." />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta name="keywords" content="home remodeling projects, kitchen remodels, bathroom renovations, adu conversions, los angeles, before and after" />
        <link rel="canonical" href="https://modernbuildremodel.com/projects" />

        {/* Open Graph */}
        <meta property="og:title" content="Featured Home Remodels Projects | Modern Build" />
        <meta property="og:description" content="Explore beautifully crafted home remodels completed by Modern Build in Southern California. From modern kitchens to elegant bathrooms and stunning ADUs, our work speaks for itself." />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/projects" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Featured Home Remodels Projects | Modern Build" />
        <meta name="twitter:description" content="See what we’ve built. Modern Build showcases luxury kitchen, bath, and ADU remodeling projects across LA. Stunning transformations. Real results." />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Projects",
                  "item": `https://modernbuildremodel.com/projects`
                },
              ]
            })
          }}
        />
      
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Our Remodeling Projects | Modern Build',
                description: 'Explore stunning home remodeling projects completed by Modern Build across Southern California.',
                slug: '/projects'
              })
            )
          }}
        />

      </Head>

      <Header />
      
      <Hero category="projects" page="page" hasContentH1={false}/>

      <section className="py-16 bg-white text-text">
        <div className="max-w-[1140px] mx-auto px-4">
          {grouped.map((group, rowIndex) => (
            group.length === 1 ? (
              // Center single project card
              <div
                key={rowIndex}
                className="flex mt-8 justify-center"
              >
                <div className="w-full lg:w-1/2">
                  <ProjectCard
                    key={`${rowIndex}-0`}
                    imageSrc={group[0].image}
                    title={group[0].title}
                    description={group[0].description}
                    link={group[0].link}
                    height="h-[338px]"
                  />
                </div>
              </div>
            ) : (
              // Grid for 2 or 3 cards
              <div
                key={rowIndex}
                className={`grid gap-8 mt-8 ${
                  group.length === 3
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2'
                }`}
              >
                {group.map((project, i) => {
                  const height = group.length === 3 ? 'h-[467px]' : 'h-[338px]';

                  return (
                    <ProjectCard
                      key={`${rowIndex}-${i}`}
                      imageSrc={project.image}
                      title={project.title}
                      description={project.description}
                      link={project.link}
                      height={height}
                    />
                  );
                })}
              </div>
            )
          ))}

        </div>
      </section>

      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
